import React, { createContext, KeyboardEvent } from 'react';
import styled from 'styled-components';
import { bindActionCreators, Dispatch } from 'redux';
import { connect } from 'react-redux';
import { reduxForm, change, getFormValues } from 'redux-form';
import { get } from 'lodash';
import Button, { ButtonGroup } from '@omilia/button';

import { FixStyle } from 'components/styled';
// @ts-ignore
import * as accountSelectors from 'store/services/accounts/selectors';
// @ts-ignore
import { checkProjectName } from 'store/services/projects/actions';
import { PartialRootState } from 'store/services/accounts/types';
import {
  FormWrapperProviderProps,
  FormWrapperOwnProps,
  FormWrapperFormValues,
  FormWrapperDispatchProps,
} from 'scenes/Projects/types';

const SForm = styled.form`
  width: 100%;

  ul {
    margin-left: 0;
  }
`;

const SButtonGroup = styled(ButtonGroup)`
  float: right;
  margin: 6px 0 9px;
`;

export const FormContext = createContext<FormWrapperProviderProps>({
  onChange: () => {},
  formValues: {},
});

const FormWrapper = ({
  children,
  handleSubmit,
  validate,
  change,
  touch,
  invalid,
  submitting,
  pristine,
  onCloseRequest,
  onSubmitForm,
  formValues,
  isSubmitDisabled,
  submitBtnName,
  ...rest
}: FormWrapperOwnProps) => {
  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Enter' && event.shiftKey === false && !invalid) {
      event.preventDefault();
      onSubmitForm(formValues);
    }
  };

  return (
    <FormContext.Provider value={{ onChange: change, formValues }}>
      <SForm
        data-test="create-project-form"
        onKeyDown={handleKeyDown}
        onSubmit={handleSubmit(onSubmitForm)}
        {...rest}
      >
        {children}
        <SButtonGroup>
          <FixStyle />
          <Button
            color="secondary"
            data-test="cancel"
            variant="text"
            onClick={onCloseRequest}
          >
            Cancel
          </Button>

          <Button
            color="primary"
            data-test="create"
            disabled={invalid || pristine || submitting || isSubmitDisabled}
            loading={submitting}
            type="submit"
            variant="contained"
          >
            {submitBtnName}
          </Button>
        </SButtonGroup>
      </SForm>
    </FormContext.Provider>
  );
};

const mapStateToProps = (
  state: PartialRootState,
  { form, initialValues }: FormWrapperOwnProps,
) => ({
  initialValues: {
    ...initialValues,
    name: '',
    project: {
      name: '',
      id: '',
    },
    files: null,
    account: accountSelectors.getSelectedAccountSelector(state) || {},
  },
  formValues: getFormValues(form)(state) || {},
});

export const mapDispatchToProps = (
  dispatch: Dispatch<FormWrapperDispatchProps>,
  { form }: FormWrapperOwnProps,
) => ({
  change: (name: string, value: string | File) =>
    dispatch(change(form, name, value)),
  checkProjectName: bindActionCreators(checkProjectName, dispatch),
});

const asyncValidate = (
  formProps: FormWrapperFormValues,
  dispatch: Dispatch<FormWrapperDispatchProps>,
  { checkProjectName }: FormWrapperDispatchProps,
) => checkProjectName(formProps.name, get(formProps, 'account.slug'));

const DecoratedFormWrapper = connect(
  mapStateToProps,
  mapDispatchToProps,
)(
  reduxForm({
    asyncValidate,
    asyncChangeFields: ['name', 'account'],
    touchOnBlur: false,
    touchOnChange: true,
  })(FormWrapper),
);

export default DecoratedFormWrapper;
